// React imports
import { useState } from "react";

// Custom queries imports
import { useAddRoleUserMutation } from "../users/useAddRoleUserMutation";
import { useRemoveRoleUserMutation } from "../users/useRemoveRoleUserMutation";
import { useCreateUserMutation } from "../users/useCreateUserMutation";
import { useCreatePersonMutation } from "../persons/useCreatePersonMutation";
import { usePersonByEmailMutation } from "../persons/usePersonByEmailMutation";
import { usePersonByUsernameMutation } from "../persons/usePersonByUsernameMutation";
import { usePersonByPhoneMutation } from "../persons/usePersonByPhoneMutation";
import { usePersonByIdCardMutation } from "../persons/usePersonByIdCardMutation";
import { useCreateStoreMutation } from "./useCreateStoreMutation";
import { useAssignPlanStoreMutation } from "../plans/useAssignPlanStoreMutation";

// Third components & helpers imports
import { toast } from "react-toastify";

// Custom types imports
import { UserRegister } from "../users/types";
import { PersonRegistration } from "../persons/types";
import { FormDataCreateStore } from "./types";

interface paramsType {
  data: {
    store: FormDataCreateStore;
    person: PersonRegistration;
    user: UserRegister;
    role: number;
    planID: number;
  };
  onSuccessMutation?: (companyID: number) => void;
}

export function useCreateOperationalStoreMutation(params: paramsType) {
  const [companyID, setCompanyID] = useState(0);
  const removeRoleUser = useRemoveRoleUserMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "success",
        render: "Usuario creado",
        autoClose: 5000,
      });
      params.onSuccessMutation?.(companyID);
    },
    (error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          error.data?.description ||
          "Algo salio mal, al intentar asignando rol",
        autoClose: 5000,
      });
    }
  );

  const addRoleUser = useAddRoleUserMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "6/6 Asignando rol...",
        autoClose: 5000,
      });
      removeRoleUser.mutate({
        email: variables.email,
        role: 1,
        notificationID: variables.notificationID,
      });
    },
    (error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          error.data?.description ||
          "Algo salio mal, al intentar asignando rol",
        autoClose: 5000,
      });
    }
  );

  const createUserMutation = useCreateUserMutation(
    (data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "6/6 Asignando rol...",
        autoClose: 5000,
      });
      addRoleUser.mutate({
        email: data.email,
        role: params.data.role,
        companyID,
        isActive: true,
        notificationID: variables.notificationID,
      });
    },
    (error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          error.data?.description ||
          "Algo salio mal, al intentar registrar el usuario",
        autoClose: 5000,
      });
    }
  );

  const createPersonMutation = useCreatePersonMutation(
    (data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "5/6 Creando usuario...",
        autoClose: 5000,
      });
      createUserMutation.mutate({
        user: {
          ...params.data.user,
          modifiedAfter: new Date().toISOString(),
          expiredBy: new Date().toISOString(),
          token: data.passwordToken,
        },
        notificationID: variables.notificationID,
      });
    },
    (error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          error.data?.description ||
          "Algo salio mal, al intentar registrar la persona",
        autoClose: 5000,
      });
    }
  );

  const assignPlanStoreMutation = useAssignPlanStoreMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        type: "success",
        render: "4/6 Creando persona...",
        autoClose: 5000,
      });
      createPersonMutation.mutate({
        person: params.data.person,
        notificationID: variables.notificationID,
      });
    },
    (error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          error.data?.description || "Hubo un error al intentar asignar plan",
        autoClose: 5000,
      });
    }
  );

  const createStoreMutation = useCreateStoreMutation(
    (data, variables) => {
      setCompanyID(data.id);
      toast.update(variables.notificationID, {
        isLoading: true,
        type: "success",
        render: "3/6 Asignado plan...",
        autoClose: 5000,
      });
      assignPlanStoreMutation.mutate({
        planID: params.data.planID,
        companyID: data.id,
        notificationID: variables.notificationID,
      });
    },
    (error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          error.data?.description || "Hubo un error al intentar crear comercio",
        autoClose: 5000,
      });
    }
  );

  const validIdCardMutation = usePersonByIdCardMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          "El documento de identidad ya está en uso, intenta de nuevo con otro documento de identidad",
        autoClose: 5000,
      });
    },
    (_error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "2/6 Creando comercio...",
        autoClose: 5000,
      });
      createStoreMutation.mutate({
        store: params.data.store,
        notificationID: variables.notificationID,
      });
    }
  );

  const validPhoneMutation = usePersonByPhoneMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          "El número telefónico ya está en uso, intenta de nuevo con otro número telefónico",
        autoClose: 5000,
      });
    },
    (_error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "1/6 Validando documento de identidad...",
        autoClose: 5000,
      });
      validIdCardMutation.mutate({
        idCard: params.data.person.idCard,
        notificationID: variables.notificationID,
      });
    }
  );

  const validUsernameMutation = usePersonByUsernameMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          "El nombre de usuario ya está en uso, intenta de nuevo con otro nombre de usuario",
        autoClose: 5000,
      });
    },
    (_error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "1/6 Validando número telefónico...",
        autoClose: 5000,
      });
      validPhoneMutation.mutate({
        phone: params.data.person.phoneNumber,
        notificationID: variables.notificationID,
      });
    }
  );

  const validEmailMutation = usePersonByEmailMutation(
    (_data, variables) => {
      toast.update(variables.notificationID, {
        isLoading: false,
        type: "error",
        render:
          "El correo electrónico ya está en uso, intenta de nuevo con otro correo electrónico",
        autoClose: 5000,
      });
    },
    (_error, variables) => {
      toast.update(variables.notificationID, {
        isLoading: true,
        render: "1/6 Validando nombre de usuario...",
        autoClose: 5000,
      });
      validUsernameMutation.mutate({
        username: params.data.user.userName,
        notificationID: variables.notificationID,
      });
    }
  );

  return validEmailMutation;
}
